import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ThunderboltFilled,
  TrophyOutlined,
  FallOutlined,
  LineChartOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ProfileOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { askAiTokens as t } from "./tokens";
import { useIsMobile } from "./useMediaQuery";

interface SuggestedQuestion {
  category: string;
  title: string;
  hint: string;
  icon: React.ReactNode;
}

const ALL_QUESTIONS: SuggestedQuestion[] = [
  {
    category: "Suppliers",
    title: "Which supplier has the best overall score?",
    hint: "Rank bidders by combined evaluation score",
    icon: <TrophyOutlined />,
  },
  {
    category: "Savings",
    title: "Summarise BAFO savings vs initial bids.",
    hint: "Negotiated price delta per supplier",
    icon: <FallOutlined />,
  },
  {
    category: "Variance",
    title: "Which materials have the highest price variance?",
    hint: "Items with the widest spread between bidders",
    icon: <LineChartOutlined />,
  },
  {
    category: "Compliance",
    title: "Are there any compliance risks I should review?",
    hint: "Surface red flags in supplier responses",
    icon: <SafetyOutlined />,
  },
  {
    category: "Delivery",
    title: "Which supplier offers the fastest delivery?",
    hint: "Compare lead times and committed schedules",
    icon: <ClockCircleOutlined />,
  },
  {
    category: "Terms",
    title: "Highlight outlier payment terms across bids.",
    hint: "Spot suppliers asking for unusual terms",
    icon: <DollarOutlined />,
  },
  {
    category: "Warranty",
    title: "Compare warranties offered by each bidder.",
    hint: "Duration, scope, and coverage gaps",
    icon: <CheckCircleOutlined />,
  },
  {
    category: "Cost",
    title: "Which supplier has the lowest landed cost?",
    hint: "Total cost of ownership incl. taxes & freight",
    icon: <ProfileOutlined />,
  },
];

const BRAND_BLUE = "#2563eb";

// Per-card level (1–4) drives the gradient darkness. Cards keep their level
// as they cycle through slots; when a new card spawns it inherits the level
// of the card that just left, so the 4-shade cycle stays stable.
const LEVEL_GRADIENTS: Record<number, string> = {
  1: "linear-gradient(140deg, #eff5ff 0%, #ffffff 100%)",
  2: "linear-gradient(140deg, #d6e5fe 0%, #f0f6fe 100%)",
  3: "linear-gradient(140deg, #b8d2fd 0%, #dfeafd 100%)",
  4: "linear-gradient(140deg, #98bbf9 0%, #cadcf6 100%)",
};
const LEVEL_BORDERS: Record<number, string> = {
  1: "rgba(37, 99, 235, 0.10)",
  2: "rgba(37, 99, 235, 0.16)",
  3: "rgba(37, 99, 235, 0.22)",
  4: "rgba(37, 99, 235, 0.28)",
};

// Slot styling: fixed positions, each slightly translated + scaled for depth.
// Slot 1 is the top (front), slot 4 is the bottom (deepest).
const CARD_HEIGHT = 112;
const SLOT_GAP = 8;
const SLOT_STEP = CARD_HEIGHT + SLOT_GAP;
const SLOT_STYLES: Record<number, { y: number; scale: number; opacity: number }> = {
  1: { y: 0, scale: 1.0, opacity: 1.0 },
  2: { y: SLOT_STEP, scale: 0.975, opacity: 0.92 },
  3: { y: SLOT_STEP * 2, scale: 0.95, opacity: 0.78 },
  4: { y: SLOT_STEP * 3, scale: 0.925, opacity: 0.6 },
};
const POS_IN = { y: -90, scale: 1, opacity: 0 };
const POS_OUT = { y: SLOT_STEP * 4 + 24, scale: 0.88, opacity: 0 };

const TICK_MS = 4500;
const TRANSITION = { duration: 0.75, ease: [0.22, 1, 0.36, 1] as const };

interface SlotCard {
  id: number; // monotonic, unique across cycles for framer keying
  content: SuggestedQuestion;
  level: 1 | 2 | 3 | 4;
  slot: 1 | 2 | 3 | 4;
}

interface Props {
  tenderTitle: string;
  onPick: (text: string) => void;
}

const heroContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.12 },
  },
};
const heroItem = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const Card: React.FC<{
  q: SuggestedQuestion;
  level: 1 | 2 | 3 | 4;
  onClick: () => void;
}> = ({ q, level, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      height: CARD_HEIGHT,
      flexShrink: 0,
      textAlign: "left",
      background: LEVEL_GRADIENTS[level],
      border: `1px solid ${LEVEL_BORDERS[level]}`,
      borderRadius: t.radius.lg,
      padding: `${t.space.s3}px ${t.space.s5}px`,
      cursor: "pointer",
      fontFamily: "inherit",
      color: "inherit",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      position: "relative",
      overflow: "hidden",
      boxShadow:
        "0 10px 24px -18px rgba(37, 99, 235, 0.45), 0 1px 2px rgba(15, 23, 42, 0.04)",
      transition: "transform 0.18s ease, box-shadow 0.18s ease",
    }}
  >
    <span
      aria-hidden
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "55%",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 100%)",
        pointerEvents: "none",
      }}
    />
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "relative",
        zIndex: 1,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 10px",
          borderRadius: t.radius.pill,
          background: "rgba(255,255,255,0.92)",
          color: BRAND_BLUE,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.3,
        }}
      >
        <span style={{ fontSize: 12 }}>{q.icon}</span>
        {q.category}
      </div>
      <ArrowRightOutlined style={{ fontSize: 13, color: BRAND_BLUE }} />
    </div>
    <div style={{ position: "relative", zIndex: 1 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: t.color.text,
          letterSpacing: -0.15,
          lineHeight: 1.4,
          marginBottom: 2,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {q.title}
      </div>
      <div
        style={{
          fontSize: 12,
          color: t.color.textMuted,
          lineHeight: 1.5,
          display: "-webkit-box",
          WebkitLineClamp: 1,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {q.hint}
      </div>
    </div>
  </button>
);

const StackConveyor: React.FC<{ onPick: (text: string) => void }> = ({
  onPick,
}) => {
  // Seed 4 cards, one per slot, with levels 1..4.
  const [cards, setCards] = useState<SlotCard[]>(() =>
    ([1, 2, 3, 4] as const).map((s) => ({
      id: s,
      content: ALL_QUESTIONS[s - 1],
      level: s,
      slot: s,
    }))
  );

  const nextIdRef = useRef(5);
  // Index of the next question to introduce.
  const nextContentRef = useRef(4);
  const pausedRef = useRef(false);
  const [hovered, setHovered] = useState(false);
  const [tabHidden, setTabHidden] = useState<boolean>(
    typeof document !== "undefined" ? document.hidden : false
  );

  // Keep pausedRef in sync without resetting the interval on every change.
  useEffect(() => {
    pausedRef.current = hovered || tabHidden;
  }, [hovered, tabHidden]);

  // Pause when the tab is hidden so we don't burn CPU offscreen.
  useEffect(() => {
    const onVis = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      setCards((prev) => {
        // Card currently at slot 4 leaves; its level is inherited by the
        // new card spawning at slot 1 so the four-shade cycle stays stable.
        const leaving = prev.find((c) => c.slot === 4);
        if (!leaving) return prev;
        const remaining = prev
          .filter((c) => c.slot !== 4)
          .map(
            (c) =>
              ({
                ...c,
                slot: (c.slot + 1) as 1 | 2 | 3 | 4,
              } as SlotCard)
          );
        const newCard: SlotCard = {
          id: nextIdRef.current,
          content:
            ALL_QUESTIONS[nextContentRef.current % ALL_QUESTIONS.length],
          level: leaving.level,
          slot: 1,
        };
        nextIdRef.current += 1;
        nextContentRef.current += 1;
        return [newCard, ...remaining];
      });
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const containerHeight = SLOT_STEP * 3 + CARD_HEIGHT + 16;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        height: containerHeight,
        overflow: "hidden",
      }}
    >
      <AnimatePresence initial={false}>
        {cards.map((card) => {
          const slotStyle = SLOT_STYLES[card.slot];
          return (
            <motion.div
              key={card.id}
              initial={POS_IN}
              animate={{
                y: slotStyle.y,
                scale: slotStyle.scale,
                opacity: slotStyle.opacity,
              }}
              exit={POS_OUT}
              transition={TRANSITION}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10 - card.slot,
              }}
            >
              <Card
                q={card.content}
                level={card.level}
                onClick={() => onPick(card.content.title)}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

const EmptyState: React.FC<Props> = ({ tenderTitle, onPick }) => {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? t.space.s5 : t.space.s8,
        padding: isMobile
          ? `${t.space.s4}px ${t.space.s4}px ${t.space.s6}px`
          : `${t.space.s6}px ${t.space.s8}px`,
        minHeight: 0,
        position: "relative",
        overflow: isMobile ? "auto" : "hidden",
        fontFamily: t.font.sans,
        alignItems: "stretch",
      }}
    >
      {/* LEFT — Hero */}
      <motion.div
        variants={heroContainer}
        initial="hidden"
        animate="show"
        style={{
          flex: isMobile ? "none" : 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          maxWidth: isMobile ? "100%" : 640,
        }}
      >
        <motion.div variants={heroItem}>
          <div
            style={{
              position: "relative",
              width: isMobile ? 48 : 64,
              height: isMobile ? 48 : 64,
              marginBottom: isMobile ? t.space.s3 : t.space.s5,
              borderRadius: "50%",
              overflow: "hidden",
              boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.06)",
            }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{
                duration: 18,
                ease: "linear",
                repeat: Infinity,
              }}
              style={{
                position: "absolute",
                inset: -16,
                background:
                  "linear-gradient(180deg, #0c1a3e 0%, #1e3a8a 22%, #2563eb 38%, #6dd5e8 56%, #f8c8b8 78%, #f1715d 100%)",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.96)",
                fontSize: isMobile ? 18 : 22,
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
              }}
            >
              <ThunderboltFilled />
            </div>
          </div>
        </motion.div>

        <motion.div variants={heroItem}>
          <h1
            style={{
              fontFamily: t.font.serif,
              fontWeight: 400,
              lineHeight: 1.08,
              color: t.color.text,
              margin: 0,
            }}
          >
            <span
              style={{
                display: "block",
                fontSize: isMobile ? 32 : 54,
                letterSpacing: isMobile ? -0.9 : -1.8,
              }}
            >
              What would you like to know about
            </span>
            <span
              style={{
                display: "block",
                fontSize: isMobile ? 24 : 34,
                letterSpacing: isMobile ? -0.5 : -0.9,
                marginTop: 4,
              }}
            >
              <span
                style={{
                  fontStyle: "italic",
                  background:
                    "linear-gradient(120deg, #60a5fa 0%, #2563eb 50%, #1e3a8a 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  whiteSpace: isMobile ? "normal" : "nowrap",
                  wordBreak: isMobile ? "break-word" : undefined,
                }}
              >
                Tender #{tenderTitle}
              </span>
              ?
            </span>
          </h1>
        </motion.div>

        <motion.div variants={heroItem}>
          <p
            style={{
              fontSize: isMobile ? 15 : 17,
              color: t.color.textMuted,
              lineHeight: 1.55,
              margin: `${isMobile ? t.space.s3 : t.space.s5}px 0 0`,
              maxWidth: 520,
            }}
          >
            Compare suppliers, summarise BAFO rounds, surface evaluation scores
            and risks — answered instantly from your live bid data.
          </p>
        </motion.div>

        <motion.div variants={heroItem}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: t.space.s2,
              padding: "5px 12px 5px 10px",
              borderRadius: t.radius.pill,
              background: t.color.surfaceMuted,
              border: `1px solid ${t.color.border}`,
              fontSize: 12,
              color: t.color.textMuted,
              marginTop: t.space.s5,
            }}
          >
            <span style={{ color: t.color.textSubtle, fontSize: 11 }}>↓</span>
            Pick a question on the right, or type your own below
          </div>
        </motion.div>
      </motion.div>

      {/* RIGHT (or BELOW on mobile) — Stacked conveyor with depth */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        style={{
          width: isMobile ? "100%" : 360,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: 0,
          maxWidth: isMobile ? "100%" : 360,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: t.space.s2,
            marginBottom: t.space.s4,
            paddingLeft: 2,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: BRAND_BLUE,
            }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1.4,
              color: t.color.textMuted,
              textTransform: "uppercase",
            }}
          >
            Popular Questions · Hover to pause
          </span>
        </div>
        <StackConveyor onPick={onPick} />
      </motion.div>
    </div>
  );
};

export default EmptyState;
